import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { X, MapPin, Mail, Phone, Loader2 } from 'lucide-react';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { API } from '../utils/config';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});


const PropertyManagersMap = ({ open, onClose }) => {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, byRegion: {} });

  useEffect(() => {
    if (open) {
      fetchMapData();
    }
  }, [open]);

  const fetchMapData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/property-managers/map-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setManagers(response.data.managers);
      
      // Calculate stats by region
      const byRegion = {};
      response.data.managers.forEach(m => {
        byRegion[m.region] = (byRegion[m.region] || 0) + 1;
      });
      
      setStats({
        total: response.data.total,
        byRegion
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching map data:', error);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] h-[90vh] p-0">
        <DialogHeader className="p-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-2xl flex items-center gap-2">
                <MapPin className="h-6 w-6 text-blue-600" />
                Mappa Responsabili Immobili
              </DialogTitle>
              <p className="text-sm text-slate-500 mt-1">
                Posizionamento geografico in base alla zona operativa
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-[calc(90vh-120px)]">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-slate-600">Caricamento dati geografici...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Stats Bar */}
            <div className="px-6 py-3 bg-slate-50 border-b">
              <div className="flex items-center gap-6 text-sm">
                <div className="font-semibold text-slate-700">
                  📊 Totale: <span className="text-blue-600">{stats.total} responsabili</span>
                </div>
                <div className="flex gap-4 flex-wrap">
                  {Object.entries(stats.byRegion)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 5)
                    .map(([region, count]) => (
                      <span key={region} className="text-slate-600">
                        {region}: <span className="font-medium">{count}</span>
                      </span>
                    ))}
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="h-[calc(90vh-180px)]">
              <MapContainer
                center={[42.5, 12.5]} // Centro Italia
                zoom={6}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {managers.map((manager) => (
                  <Marker
                    key={manager.id}
                    position={[manager.latitude, manager.longitude]}
                  >
                    <Popup>
                      <div className="min-w-[250px]">
                        <h3 className="font-semibold text-lg mb-2 text-slate-800">
                          {manager.name}
                        </h3>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="font-medium text-slate-700">Zona Operativa</div>
                              <div className="text-slate-600">{manager.operation_area_city}</div>
                              <div className="text-xs text-slate-500">{manager.region}</div>
                            </div>
                          </div>
                          
                          {manager.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-slate-500 flex-shrink-0" />
                              <a 
                                href={`mailto:${manager.email}`}
                                className="text-blue-600 hover:underline break-all"
                              >
                                {manager.email}
                              </a>
                            </div>
                          )}
                          
                          {manager.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-slate-500 flex-shrink-0" />
                              <a 
                                href={`tel:${manager.phone}`}
                                className="text-slate-700 hover:underline"
                              >
                                {manager.phone}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>

            {managers.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                <div className="text-center">
                  <MapPin className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 font-medium">Nessun responsabile con zona operativa</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Aggiungi la zona operativa ai responsabili per visualizzarli sulla mappa
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PropertyManagersMap;
